import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { PackagePlus, Save } from "lucide-react";
import { toast } from "sonner";
import { httpServices as services } from "@/services";
import type { InputCatalogueItem } from "@/types/domain";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { ErrorState, LoadingState } from "@/components/shared/StatePanels";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

type TrackingMode = "INVENTORY" | "NURSERY";

const emptyForm = {
  code: "",
  name: "",
  category: "",
  unit: "",
  description: "",
  trackingMode: "INVENTORY" as TrackingMode,
};

export default function InputsPage() {
  const [items, setItems] = useState<InputCatalogueItem[]>();
  const [loadError, setLoadError] = useState("");
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState<string[]>([]);

  const load = async () => {
    try {
      setLoadError("");
      const result = await services.inputs.listCatalogue({ pageSize: 100 });
      setItems(result.items);
    } catch (error) {
      setLoadError((error as Error).message || "Input catalogue could not be loaded.");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const set = (key: keyof typeof form, value: string) =>
    setForm((current) => ({ ...current, [key]: value }));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const next = [
      !form.code.trim() && "Item code is required.",
      !form.name.trim() && "Item name is required.",
      !form.category.trim() && "Category is required.",
      !form.unit.trim() && "Default unit is required.",
    ].filter(Boolean) as string[];

    setFormErrors(next);
    if (next.length) return;

    setSaving(true);
    try {
      const created = await services.inputs.createCatalogueItem({
        itemCode: form.code.trim(),
        itemName: form.name.trim(),
        category: form.category.trim(),
        defaultUnit: form.unit.trim(),
        description: form.description.trim() || undefined,
        stockTrackingMode: form.trackingMode,
        active: true,
      });
      setItems((current) => [created, ...(current || [])]);
      setCreating(false);
      setForm(emptyForm);
      setFormErrors([]);
      toast.success("Input catalogue item created", {
        description:
          created.stockTrackingMode === "NURSERY"
            ? "This item is managed through Nursery living stock and Seedling Distribution Plans."
            : "This item can now be received into Inventory and distributed to exact Plots.",
      });
    } catch (error) {
      toast.error("Could not create input item", { description: (error as Error).message });
    } finally {
      setSaving(false);
    }
  };

  if (loadError) return <ErrorState message={loadError} onRetry={load} />;
  if (!items) return <LoadingState label="Loading input catalogue" />;

  return (
    <div className="space-y-6" data-testid="inputs-page">
      <PageHeader
        eyebrow="Inputs & Stock"
        title="Input Catalogue"
        description="Define agricultural inputs once, then manage physical stock in Inventory and operational allocation in Distributions."
        action={
          <Button
            onClick={() => {
              setCreating((value) => !value);
              setFormErrors([]);
            }}
            data-testid="inputs-add-item-button"
          >
            <PackagePlus className="mr-2 size-4" />
            Add item
          </Button>
        }
      />

      {creating && (
        <Card data-testid="inputs-create-panel">
          <CardContent className="p-5">
            <form onSubmit={submit} className="space-y-5" data-testid="inputs-create-form">
              {formErrors.length > 0 && (
                <div
                  className="rounded-lg border border-destructive/30 p-3 text-sm text-destructive"
                  data-testid="inputs-form-errors"
                >
                  {formErrors.join(" ")}
                </div>
              )}

              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="input-code">Item code</Label>
                  <Input
                    id="input-code"
                    value={form.code}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => set("code", event.target.value)}
                    placeholder="e.g. FERT-NPK-171717"
                    data-testid="inputs-code"
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="input-name">Item name</Label>
                  <Input
                    id="input-name"
                    value={form.name}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => set("name", event.target.value)}
                    placeholder="e.g. NPK 17:17:17 fertilizer"
                    data-testid="inputs-name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="input-category">Category</Label>
                  <Input
                    id="input-category"
                    value={form.category}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => set("category", event.target.value)}
                    placeholder="Fertilizer, Agrochemical, Seedling..."
                    data-testid="inputs-category"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="input-unit">Default unit</Label>
                  <Input
                    id="input-unit"
                    value={form.unit}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => set("unit", event.target.value)}
                    placeholder="kg, litre, seedling..."
                    data-testid="inputs-unit"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Stock tracking</Label>
                  <Select value={form.trackingMode} onValueChange={(value: string) => set("trackingMode", value as TrackingMode)}>
                    <SelectTrigger data-testid="inputs-tracking-mode">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="INVENTORY">Inventory stock</SelectItem>
                      <SelectItem value="NURSERY">Nursery living stock</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2 md:col-span-3">
                  <Label htmlFor="input-description">Description / specification</Label>
                  <Input
                    id="input-description"
                    value={form.description}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => set("description", event.target.value)}
                    placeholder="Optional product or operational specification"
                    data-testid="inputs-description"
                  />
                </div>
              </div>

              <div className="rounded-lg border bg-muted/30 p-3 text-xs text-muted-foreground">
                Inventory items can be received, transferred and issued through stock control. Nursery items remain quantity-first living stock and use the separate Seedling Distribution Plan workflow; variety remains optional.
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setCreating(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving} data-testid="inputs-save">
                  <Save className="mr-2 size-4" />
                  {saving ? "Saving..." : "Create item"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          <Table data-testid="inputs-catalogue-table">
            <TableHeader>
              <TableRow>
                <TableHead>Item</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Unit</TableHead>
                <TableHead>Tracking</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Operational note</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id} data-testid={`input-row-${item.id}`}>
                  <TableCell>
                    <p className="font-semibold">{item.name}</p>
                    <p className="font-mono text-xs text-muted-foreground">{item.code}</p>
                  </TableCell>
                  <TableCell>{item.category}</TableCell>
                  <TableCell>{item.unit}</TableCell>
                  <TableCell>{item.stockTrackingMode === "NURSERY" ? "Nursery" : "Inventory"}</TableCell>
                  <TableCell>
                    <StatusBadge status={item.status} testId={`input-status-${item.id}`} />
                  </TableCell>
                  <TableCell className="max-w-sm text-sm text-muted-foreground">{item.inventoryNote}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
